<template>
  <v-row>
    <v-col cols="12">
      <v-card class="brc-card" flat>
        <!-- HEADER BAND -->
        <div class="brc-header pl-base-bg">
          <div class="brc-header-icon">
            <v-icon color="#fff" size="22">mdi-bank-check</v-icon>
          </div>
          <div>
            <div class="brc-title">Bank Reconciliation</div>
            <div class="brc-subtitle">Match your recorded transactions against your actual bank statement</div>
          </div>
        </div>

        <div class="pa-6">
          <v-alert type="info" outlined dense border="left" class="brc-disclaimer">
            Matching only ever updates the imported bank statement line —
            your Transactions and ledger entries are never changed. Ignore
            a line for things like bank charges or interest that don't
            correspond to any recorded Transaction.
          </v-alert>

          <template v-if="summary && summary.accounts.length">
            <v-row dense class="mb-4">
              <v-col cols="12" sm="6" md="3" v-for="acct in summary.accounts" :key="acct.accountId">
                <v-card class="brc-kpi-card pa-4" flat>
                  <div class="brc-kpi-label">{{ acct.accountName }}</div>
                  <div class="brc-kpi-row">
                    <span>Book</span>
                    <strong>{{ formatINR(acct.bookBalance) }}</strong>
                  </div>
                  <div class="brc-kpi-row">
                    <span>Bank</span>
                    <strong>{{ acct.bankBalance !== null ? formatINR(acct.bankBalance) : "No statement balance" }}</strong>
                  </div>
                  <div class="brc-kpi-row brc-kpi-row--diff" :class="{ 'brc-kpi-row--zero': acct.difference === 0 }">
                    <span>Difference</span>
                    <strong>{{ acct.difference !== null ? formatINR(acct.difference) : "-" }}</strong>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card class="brc-kpi-card brc-kpi-card--status pa-4" flat>
                  <div class="brc-kpi-label">Outstanding Lines</div>
                  <div class="brc-kpi-row">
                    <span>Unmatched</span>
                    <strong>{{ summary.unmatchedCount }}</strong>
                  </div>
                  <div class="brc-kpi-row">
                    <span>Suggested</span>
                    <strong>{{ summary.suggestedCount }}</strong>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <div class="brc-filter-bar">
            <v-row dense align="center">
              <v-col cols="12" md="4">
                <v-select
                  v-model="statusFilter"
                  :items="statusOptions"
                  label="Status"
                  outlined
                  dense
                  hide-details
                  @change="loadLines"
                />
              </v-col>
              <v-col cols="12" md="8" class="brc-actions">
                <v-btn outlined color="#1d6f42" class="mr-2" @click="downloadExcel">
                  <v-icon left size="16" color="#1d6f42">mdi-file-excel-box</v-icon>
                  Excel
                </v-btn>
                <v-btn outlined color="#0a5a90" class="mr-2" :loading="rematching" @click="doRematch">
                  <v-icon left size="16">mdi-refresh</v-icon>
                  Re-run Auto-Match
                </v-btn>
                <v-btn class="brc-import-btn" @click="importDialog = true">
                  <v-icon left size="16">mdi-upload</v-icon>
                  Import Statement
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <app-ag-grid
            title="Bank Statement Lines"
            :columnDefs="lineColumnDefs"
            :rowData="lines"
            :newBtn="false"
            :refreshBtn="false"
            :mobileCard="lineMobileCard"
            height="height: 480px"
            class="mb-6"
          />

          <!-- IMPORT HISTORY -->
          <div class="brc-section-title">Import History</div>
          <v-simple-table v-if="!$vuetify.breakpoint.smAndDown" class="brc-table">
            <thead>
              <tr>
                <th>File</th>
                <th class="text-right">Imported</th>
                <th class="text-right">Skipped (duplicate)</th>
                <th>Imported On</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="batch in importHistory" :key="batch._id">
                <td>{{ batch.fileName || "-" }}</td>
                <td class="text-right">{{ batch.rowCount }}</td>
                <td class="text-right">{{ batch.skippedCount }}</td>
                <td>{{ formatDate(batch.createdAt) }}</td>
              </tr>
              <tr v-if="!importHistory.length">
                <td colspan="4" class="brc-empty-cell">No imports yet</td>
              </tr>
            </tbody>
          </v-simple-table>

          <!-- Mobile fallback: small historical list, not worth a second AppAgGrid toolbar -->
          <div v-else class="brc-import-history-cards">
            <div v-if="!importHistory.length" class="brc-empty-state">No imports yet</div>
            <div v-for="batch in importHistory" :key="batch._id" class="brc-import-history-card">
              <div class="brc-import-history-card-title">{{ batch.fileName || "-" }}</div>
              <div class="brc-import-history-card-fields">
                <span>Imported: {{ batch.rowCount }}</span>
                <span>Skipped: {{ batch.skippedCount }}</span>
                <span>{{ formatDate(batch.createdAt) }}</span>
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </v-col>

    <!-- IMPORT DIALOG -->
    <v-dialog v-model="importDialog" max-width="640" persistent>
      <v-card>
        <v-card-title>Import Bank Statement</v-card-title>
        <v-card-text>
          <v-file-input
            v-model="file"
            label="Bank Statement (CSV or Excel)"
            accept=".xlsx,.xls,.csv"
            outlined
            dense
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
            @change="onFileSelected"
          />

          <div v-if="parsing" class="brc-empty-state">Parsing file...</div>

          <template v-if="parsedResult">
            <v-alert v-if="parsedResult.errors.length" type="warning" outlined dense class="mb-3">
              {{ parsedResult.errors.length }} row(s) skipped due to errors.
            </v-alert>
            <div class="brc-preview-summary mb-3">
              <strong>{{ parsedResult.rows.length }}</strong> row(s) ready to import
            </div>
            <v-simple-table dense class="brc-preview-table" v-if="parsedResult.rows.length">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th class="text-right">Debit</th>
                  <th class="text-right">Credit</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in previewRows" :key="i">
                  <td>{{ row.date }}</td>
                  <td>{{ row.description }}</td>
                  <td class="text-right">{{ row.debit ? formatINR(row.debit) : "-" }}</td>
                  <td class="text-right">{{ row.credit ? formatINR(row.credit) : "-" }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-if="parsedResult.rows.length > previewRows.length" class="brc-preview-more">
              + {{ parsedResult.rows.length - previewRows.length }} more row(s)
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeImportDialog">Cancel</v-btn>
          <v-btn class="brc-import-btn" :disabled="!canImport" :loading="importing" @click="doImport">
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- MATCH DIALOG -->
    <v-dialog v-model="matchDialog" max-width="640">
      <v-card>
        <v-card-title>Match Transaction</v-card-title>
        <v-card-text>
          <div v-if="lineToMatch" class="brc-preview-summary mb-3">
            {{ formatDate(lineToMatch.date) }} — {{ lineToMatch.description || "-" }} —
            {{ formatINR(lineToMatch.debit || lineToMatch.credit) }}
          </div>

          <div v-if="loadingCandidates" class="brc-empty-state">Loading candidates...</div>

          <v-simple-table v-else dense class="brc-preview-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th>Payment Method</th>
                <th class="text-right">Amount</th>
                <th class="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="txn in candidates" :key="txn._id">
                <td>{{ formatDate(txn.date) }}</td>
                <td>{{ txn.category }}</td>
                <td>{{ txn.paymentMethod }}</td>
                <td class="text-right">{{ formatINR(txn.amount) }}</td>
                <td class="text-right">
                  <v-btn small class="brc-import-btn" @click="doConfirmMatch(txn)">Select</v-btn>
                </td>
              </tr>
              <tr v-if="!candidates.length">
                <td colspan="5" class="brc-empty-cell">No matching transactions found within a 3-day window</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="matchDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import AppAgGrid from "@/components/common/ag-grid.vue";
import { parseBankStatement } from "@/utils/bank-statement-parser";
import { downloadMultiSheetWorkbook } from "@/utils/xlsx-export";

export default {
  name: "BankReconciliationPage",
  components: { AppAgGrid },

  data() {
    return {
      statusFilter: "",
      statusOptions: [
        { text: "All", value: "" },
        { text: "Unmatched", value: "UNMATCHED" },
        { text: "Suggested", value: "SUGGESTED" },
        { text: "Confirmed", value: "CONFIRMED" },
        { text: "Ignored", value: "IGNORED" },
      ],

      importDialog: false,
      file: null,
      parsing: false,
      parsedResult: null,
      importing: false,
      rematching: false,

      matchDialog: false,
      lineToMatch: null,
      loadingCandidates: false,

      lineColumnDefs: [
        { headerName: "Date", field: "date", valueFormatter: (p) => this.formatDate(p.value), minWidth: 110 },
        { headerName: "Description", field: "description", valueFormatter: (p) => p.value || "-", minWidth: 180 },
        { headerName: "Reference", field: "referenceNumber", valueFormatter: (p) => p.value || "-", minWidth: 120 },
        { headerName: "Debit", field: "debit", valueFormatter: (p) => (p.value ? this.formatINR(p.value) : "-"), minWidth: 120 },
        { headerName: "Credit", field: "credit", valueFormatter: (p) => (p.value ? this.formatINR(p.value) : "-"), minWidth: 120 },
        { headerName: "Status", field: "matchStatus", minWidth: 120 },
        {
          headerName: "Matched Transaction",
          field: "matchedTransactionId",
          valueFormatter: (p) => (p.value ? `${p.value.customerName || p.value.category} (${this.formatINR(p.value.amount)})` : "-"),
          minWidth: 200,
        },
        {
          headerName: "Action",
          field: "_id",
          minWidth: 220,
          sortable: false,
          filter: false,
          cellRenderer: (params) => {
            const status = params.data.matchStatus;
            const btnStyle = "cursor:pointer;font-weight:600;font-size:12px;";
            const buttons = [];
            if (status === "SUGGESTED") {
              buttons.push(`<span data-action="confirm" style="${btnStyle}color:#15803d">Confirm</span>`);
              buttons.push(`<span data-action="match" style="${btnStyle}color:#0a5a90">Change</span>`);
            }
            if (status === "UNMATCHED") {
              buttons.push(`<span data-action="match" style="${btnStyle}color:#0a5a90">Match</span>`);
            }
            if (status === "CONFIRMED") {
              buttons.push(`<span data-action="unmatch" style="${btnStyle}color:#d93025">Unmatch</span>`);
            }
            if (status === "UNMATCHED" || status === "SUGGESTED") {
              buttons.push(`<span data-action="ignore" style="${btnStyle}color:#475569">Ignore</span>`);
            }
            if (status === "IGNORED") {
              buttons.push(`<span data-action="unmatch" style="${btnStyle}color:#0a5a90">Restore</span>`);
            }
            return buttons.join('<span style="display:inline-block;width:12px"></span>');
          },
          onCellClicked: (params) => {
            const action = params.event.target.dataset.action;
            if (!action) return;
            if (action === "confirm") this.doConfirmSuggested(params.data);
            else if (action === "match") this.openMatchDialog(params.data);
            else if (action === "ignore") this.doIgnore(params.data);
            else if (action === "unmatch") this.doUnmatch(params.data);
          },
        },
      ],
    };
  },

  computed: {
    lines() {
      return this.$store.state.bankReconciliation.lines;
    },
    importHistory() {
      return this.$store.state.bankReconciliation.importHistory;
    },
    candidates() {
      return this.$store.state.bankReconciliation.candidates;
    },
    summary() {
      return this.$store.state.bankReconciliation.summary;
    },
    previewRows() {
      return this.parsedResult ? this.parsedResult.rows.slice(0, 20) : [];
    },
    canImport() {
      return !!(this.parsedResult && this.parsedResult.rows.length && !this.importing);
    },

    lineMobileCard() {
      const statusColors = {
        UNMATCHED: { background: "#fef3c7", color: "#92400e" },
        CONFIRMED: { background: "#d1fae5", color: "#065f46" },
        IGNORED: { background: "#e2e8f0", color: "#475569" },
        SUGGESTED: { background: "#dbeafe", color: "#1e40af" },
      };

      return {
        title: (row) => row.description || row.referenceNumber || "-",
        subtitle: (row) => this.formatDate(row.date),
        badge: (row) => ({
          text: row.matchStatus,
          style: statusColors[row.matchStatus] || { background: "#f1f5f9", color: "#475569" },
        }),
        fields: (row) => {
          const fields = [
            { label: "Debit", value: row.debit ? this.formatINR(row.debit) : "-" },
            { label: "Credit", value: row.credit ? this.formatINR(row.credit) : "-" },
          ];
          if (row.matchedTransactionId) {
            fields.push({
              label: "Matched",
              value: `${row.matchedTransactionId.customerName || row.matchedTransactionId.category} (${this.formatINR(row.matchedTransactionId.amount)})`,
            });
          }
          return fields;
        },
        actions: (row) => {
          const actions = [];
          if (row.matchStatus === "SUGGESTED") {
            actions.push({ label: "Confirm", icon: "mdi-check", color: "#15803d", onClick: () => this.doConfirmSuggested(row) });
          }
          if (row.matchStatus === "UNMATCHED" || row.matchStatus === "SUGGESTED") {
            actions.push({
              label: row.matchStatus === "SUGGESTED" ? "Change" : "Match",
              icon: "mdi-link-variant",
              color: "#0a5a90",
              onClick: () => this.openMatchDialog(row),
            });
          }
          if (row.matchStatus === "CONFIRMED") {
            actions.push({ label: "Unmatch", icon: "mdi-link-variant-off", color: "#d93025", onClick: () => this.doUnmatch(row) });
          }
          if (row.matchStatus === "UNMATCHED" || row.matchStatus === "SUGGESTED") {
            actions.push({ label: "Ignore", icon: "mdi-eye-off-outline", color: "#475569", onClick: () => this.doIgnore(row) });
          }
          if (row.matchStatus === "IGNORED") {
            actions.push({ label: "Restore", icon: "mdi-restore", color: "#0a5a90", onClick: () => this.doUnmatch(row) });
          }
          return actions;
        },
      };
    },
  },

  created() {
    this.loadLines();
    this.loadHistory();
    this.loadSummary();
  },

  methods: {
    formatINR(value) {
      if (value === null || value === undefined) return "0";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value);
    },

    formatDate(value) {
      if (!value) return "-";
      const d = new Date(value);
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    },

    async loadLines() {
      try {
        await this.$store.dispatch("bankReconciliation/getLines", {
          matchStatus: this.statusFilter || undefined,
        });
      } catch (err) {
        console.error(err);
        this.$toast?.error("Failed to load bank statement lines");
      }
    },

    async loadHistory() {
      try {
        await this.$store.dispatch("bankReconciliation/getImportHistory");
      } catch (err) {
        console.error(err);
        this.$toast?.error("Failed to load import history");
      }
    },

    async loadSummary() {
      try {
        await this.$store.dispatch("bankReconciliation/getSummary");
      } catch (err) {
        console.error(err);
        this.$toast?.error("Failed to load reconciliation summary");
      }
    },

    async onFileSelected(file) {
      this.parsedResult = null;
      if (!file) return;

      this.parsing = true;
      try {
        const arrayBuffer = await file.arrayBuffer();
        this.parsedResult = parseBankStatement(arrayBuffer);

        if (!this.parsedResult.rows.length) {
          this.$toast?.error("No valid rows found in this file");
        }
      } catch (err) {
        console.error(err);
        this.$toast?.error("Failed to parse file — is this a valid bank statement export?");
        this.parsedResult = null;
      } finally {
        this.parsing = false;
      }
    },

    async doImport() {
      this.importing = true;
      try {
        const result = await this.$store.dispatch("bankReconciliation/importStatement", {
          fileName: this.file?.name,
          rows: this.parsedResult.rows,
        });

        this.$toast?.success(`Imported ${result.imported} row(s), skipped ${result.skipped} duplicate(s)`);
        this.closeImportDialog();
        this.loadSummary();
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to import");
      } finally {
        this.importing = false;
      }
    },

    closeImportDialog() {
      this.importDialog = false;
      this.file = null;
      this.parsedResult = null;
    },

    async openMatchDialog(line) {
      this.lineToMatch = line;
      this.matchDialog = true;
      this.loadingCandidates = true;
      try {
        await this.$store.dispatch("bankReconciliation/getCandidates", line._id);
      } catch (err) {
        console.error(err);
        this.$toast?.error("Failed to load match candidates");
      } finally {
        this.loadingCandidates = false;
      }
    },

    async doConfirmMatch(txn) {
      try {
        await this.$store.dispatch("bankReconciliation/confirmMatch", {
          lineId: this.lineToMatch._id,
          transactionId: txn._id,
        });
        this.$toast?.success("Matched successfully");
        this.matchDialog = false;
        this.loadSummary();
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to confirm match");
      }
    },

    async doUnmatch(line) {
      try {
        await this.$store.dispatch("bankReconciliation/unmatch", line._id);
        this.$toast?.success("Unmatched successfully");
        this.loadSummary();
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to unmatch");
      }
    },

    async doIgnore(line) {
      try {
        await this.$store.dispatch("bankReconciliation/ignoreLine", line._id);
        this.$toast?.success("Line ignored");
        this.loadSummary();
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to ignore line");
      }
    },

    async doConfirmSuggested(line) {
      try {
        await this.$store.dispatch("bankReconciliation/confirmMatch", {
          lineId: line._id,
          transactionId: line.matchedTransactionId._id,
        });
        this.$toast?.success("Confirmed successfully");
        this.loadSummary();
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to confirm");
      }
    },

    async doRematch() {
      this.rematching = true;
      try {
        const result = await this.$store.dispatch("bankReconciliation/rematch");
        this.$toast?.success(`Suggested ${result.suggested} match(es) out of ${result.checked} unmatched line(s)`);
        await this.loadLines();
        this.loadSummary();
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to re-run auto-match");
      } finally {
        this.rematching = false;
      }
    },

    downloadExcel() {
      const rows = this.lines.map((line) => [
        this.formatDate(line.date),
        line.description || "-",
        line.referenceNumber || "-",
        Number(line.debit) || 0,
        Number(line.credit) || 0,
        line.matchStatus,
        line.matchedTransactionId ? (line.matchedTransactionId.customerName || line.matchedTransactionId.category) : "-",
        line.matchedTransactionId ? Number(line.matchedTransactionId.amount) || 0 : "",
      ]);

      const summaryRows = (this.summary?.accounts || []).map((a) => [
        a.accountName,
        Number(a.bookBalance) || 0,
        a.bankBalance !== null ? Number(a.bankBalance) : "No statement balance",
        a.difference !== null ? Number(a.difference) : "-",
      ]);

      downloadMultiSheetWorkbook(
        [
          {
            name: "Summary",
            title: "Bank Reconciliation Summary",
            headers: ["Account", "Book Balance", "Bank Balance", "Difference"],
            rows: summaryRows,
            columnWidths: [22, 16, 18, 16],
            numberColumns: [1, 2, 3],
          },
          {
            name: "Statement Lines",
            title: "Bank Statement Lines",
            headers: ["Date", "Description", "Reference", "Debit", "Credit", "Status", "Matched Transaction", "Amount"],
            rows,
            columnWidths: [12, 26, 14, 14, 14, 12, 24, 14],
            numberColumns: [3, 4, 7],
          },
        ],
        "bank-reconciliation.xlsx",
      );
    },
  },
};
</script>

<style scoped>
.brc-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.brc-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.brc-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.brc-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.brc-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.brc-disclaimer {
  margin-bottom: 20px;
  font-size: 13px;
}

.brc-filter-bar {
  margin-bottom: 20px;
}

.brc-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.brc-import-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.brc-kpi-card {
  border-radius: 0;
  border: 1px solid #eef1f5;
  box-shadow: 0 6px 16px rgba(15, 30, 50, 0.06);
}

.brc-kpi-card--status {
  background: #f8fafc;
}

.brc-kpi-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 8px;
}

.brc-kpi-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #334155;
  padding: 3px 0;
}

.brc-kpi-row--diff strong {
  color: #b23a3a;
}

.brc-kpi-row--diff.brc-kpi-row--zero strong {
  color: #15803d;
}

.brc-section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 10px;
}

.brc-table,
.brc-preview-table {
  border: 1px solid #eef1f5;
  border-radius: 0;
}

.brc-empty-cell {
  text-align: center;
  color: #94a3b8;
  padding: 16px 0 !important;
}

.brc-empty-state {
  text-align: center;
  padding: 24px 0;
  color: #94a3b8;
  font-size: 14px;
}

.brc-preview-summary {
  font-size: 13px;
  color: #334155;
}

.brc-preview-more {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 6px;
  text-align: center;
}

.brc-import-history-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.brc-import-history-card {
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 12px 14px;
  background: #fbfbfd;
}

.brc-import-history-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.brc-import-history-card-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 6px;
  font-size: 12.5px;
  color: #64748b;
}
</style>
